/**
 * 监控指标展示格式化
 *
 * 核心约定：NULL ≠ 0
 * metric_daily 中指标为 NULL 表示「该指标当日未取到」，与「值为 0」语义不同。
 * 前者展示「-」，后者展示「0」。混淆会让 DSP 误以为被冤枉。
 */
import React from 'react';
import {MONITOR_THRESHOLDS} from '../config/exemptSpotCheckConfig';

/** 指标缺失占位符 */
export const EMPTY_METRIC = '-';

/** 数值型指标：区分 undefined/null（未取到）与 0（真实为零） */
export const formatCount = (value?: number | null, suffix = ''): string => {
    if (value === undefined || value === null) {
        return EMPTY_METRIC;
    }
    return `${value}${suffix}`;
};

/** 比率型指标：入参为小数（0.078 → 7.8%） */
export const formatRate = (value?: number | null, fractionDigits = 1): string => {
    if (value === undefined || value === null) {
        return EMPTY_METRIC;
    }
    return `${(value * 100).toFixed(fractionDigits)}%`;
};

/** 环比：带正负号 */
export const formatDeltaRate = (value?: number | null): string => {
    if (value === undefined || value === null) {
        return EMPTY_METRIC;
    }
    const percent = (value * 100).toFixed(0);
    return value >= 0 ? `+${percent}%` : `${percent}%`;
};

/** 超阈值的指标标红，未取到的指标用灰色占位 */
const metricClass = (value: number | null | undefined, isAbnormal: boolean): string => {
    if (value === undefined || value === null) {
        return 'text-gray-300';
    }
    return isAbnormal ? 'text-red-600 font-semibold' : 'text-gray-700';
};

/** 客诉数单元格 */
/** 客诉件数单元格：件数不参与判级，不标红 */
export const renderComplaintCount = (value?: number | null): React.ReactNode => (
    <span className={metricClass(value, false)}>{formatCount(value, ' 件')}</span>
);

/** 客诉环比单元格：高风险档标红、需关注档标橙，负增长标绿 */
export const renderDeltaRate = (value?: number | null): React.ReactNode => {
    if (value === undefined || value === null) {
        return <span className="text-gray-300">{EMPTY_METRIC}</span>;
    }
    if (value > MONITOR_THRESHOLDS.highRiskDeltaRate) {
        return <span className="text-red-600 font-semibold">{formatDeltaRate(value)}</span>;
    }
    if (value > MONITOR_THRESHOLDS.attentionDeltaRate) {
        return <span className="text-amber-600">{formatDeltaRate(value)}</span>;
    }
    if (value < 0) {
        return <span className="text-green-600">{formatDeltaRate(value)}</span>;
    }
    return <span className="text-gray-700">{formatDeltaRate(value)}</span>;
};

/** 围栏地址数：物化挂载完成前为空，展示「统计中」 */
export const formatAddressCount = (
    addressCount: number | undefined,
    mountingText: string,
): string => {
    if (addressCount === undefined || addressCount === null) {
        return mountingText;
    }
    return `${addressCount}`;
};
